export type SSEEvent = {
  event: string;
  data: string;
};

export type SSEConnectionOptions = {
  url: string;
  method?: 'GET' | 'POST';
  headers?: Record<string, string>;
  body?: string;
  onEvent: (event: SSEEvent) => void;
  onError?: (error: Error & { status?: number }) => void;
  onComplete?: () => void;
};

export type SSEConnection = {
  abort: () => void;
};

/** How long a stream may stay silent before it is given up. */
const kIdleTimeoutMs = 60000;

function parseSSEBlock(block: string): SSEEvent | null {
  let eventType = 'message';
  let data = '';

  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) {
      eventType = line.substring(6).trim();
    } else if (line.startsWith('data:')) {
      data = line.substring(5).trimStart();
    }
  }

  return data ? { event: eventType, data } : null;
}

export function createSSEConnection(
  options: SSEConnectionOptions,
): SSEConnection {
  const xhr = new XMLHttpRequest();
  let lastIndex = 0;
  let buffer = '';
  let eventCount = 0;
  let progressCount = 0;
  const t0 = Date.now();

  const log = (msg: string, ...args: any[]) => {
    if (__DEV__) {
      console.log(`[SSE ${Date.now() - t0}ms] ${msg}`, ...args);
    }
  };

  log('open %s %s', options.method ?? 'POST', options.url);

  xhr.open(options.method ?? 'POST', options.url, true);
  xhr.setRequestHeader('Accept', 'text/event-stream');
  xhr.setRequestHeader('Cache-Control', 'no-cache');

  if (options.headers) {
    for (const [key, value] of Object.entries(options.headers)) {
      xhr.setRequestHeader(key, value);
    }
  }
  if (options.body) {
    xhr.setRequestHeader('Content-Type', 'application/json');
  }

  // A reply may run for minutes while the model works; what ends it is
  // silence. The gateway writes a keep-alive every 15s, so a minute with
  // nothing at all is a dead connection.
  let idle: ReturnType<typeof setTimeout> | null = null;
  const stopIdle = () => {
    if (idle) clearTimeout(idle);
    idle = null;
  };
  const watchIdle = () => {
    stopIdle();
    idle = setTimeout(() => {
      log('idle for %dms after %d events', kIdleTimeoutMs, eventCount);
      xhr.abort();
      options.onError?.(new Error('SSE connection timed out'));
    }, kIdleTimeoutMs);
  };

  xhr.onprogress = () => {
    watchIdle();
    progressCount++;
    const newText = xhr.responseText.substring(lastIndex);
    lastIndex = xhr.responseText.length;
    buffer += newText;

    const parts = buffer.split('\n\n');
    buffer = parts.pop()!;

    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const event = parseSSEBlock(trimmed);
      if (event) {
        eventCount++;
        if (eventCount <= 5 || eventCount % 20 === 0) {
          log(
            'event #%d type=%s len=%d (progress #%d)',
            eventCount,
            event.event,
            event.data.length,
            progressCount,
          );
        }
        options.onEvent(event);
      }
    }
  };

  xhr.onload = () => {
    stopIdle();
    log(
      'onload status=%d events=%d progress=%d',
      xhr.status,
      eventCount,
      progressCount,
    );
    // ANY non-2xx is an error, not a completion. Only 401 was special-cased,
    // so a 500 or 404 fell through: the JSON error body is not a valid SSE
    // block, `parseSSEBlock` returned null, and `onComplete` fired while
    // `onError` never did — leaving a caller waiting on a terminal event that
    // was never coming, with nothing to show and nothing to retry.
    if (xhr.status < 200 || xhr.status >= 300) {
      const err: Error & { status?: number } = new Error(
        xhr.status === 401 ? 'unauthorized' : `stream failed (${xhr.status})`,
      );
      err.status = xhr.status;
      options.onError?.(err);
      return;
    }
    if (buffer.trim()) {
      const event = parseSSEBlock(buffer.trim());
      if (event) {
        log('final event type=%s len=%d', event.event, event.data.length);
        options.onEvent(event);
      }
      buffer = '';
    }
    options.onComplete?.();
  };

  xhr.onerror = () => {
    stopIdle();
    log('onerror after %d events', eventCount);
    options.onError?.(new Error('SSE connection failed'));
  };

  watchIdle();
  xhr.send(options.body ?? null);

  return {
    abort: () => {
      log('abort after %d events', eventCount);
      stopIdle();
      xhr.abort();
    },
  };
}
